import { Routes, Route, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import { Tabs, TabsList, TabsTrigger, Skeleton } from '@vnetwork/ui/primitives';
import { useWaapWebsite } from '@/pages/services/waap/waap-context';
import { WaapConsoleBreadcrumbs } from '@/pages/services/waap/WaapConsoleBreadcrumbs';
import { ConfigurationTab } from '@/pages/services/waap/website/ConfigurationTab';
import { OriginTab } from '@/pages/services/waap/website/OriginTab';
import { CacheControlTab } from '@/pages/services/waap/website/CacheControlTab';
import { AccessControlTab } from '@/pages/services/waap/website/AccessControlTab';
import { SslTab } from '@/pages/services/waap/website/SslTab';
import { CorsHeaderTab } from '@/pages/services/waap/website/CorsHeaderTab';
import { PurgeTab } from '@/pages/services/waap/website/PurgeTab';
import { PrefetchTab } from '@/pages/services/waap/website/PrefetchTab';
import { RedirectionTab } from '@/pages/services/waap/website/RedirectionTab';

/** Per-website config tabs — mirrors the SwiftFederation website-detail tab bar. */
const TABS = [
  { slug: 'configuration', label: 'Configuration' },
  { slug: 'origin', label: 'Origin' },
  { slug: 'access-control', label: 'Access Control' },
  { slug: 'cache-control', label: 'Cache Control' },
  { slug: 'redirection', label: 'Redirection' },
  { slug: 'ssl', label: 'SSL Certificate' },
  { slug: 'cors', label: 'CORS Header' },
  { slug: 'purge', label: 'Purge' },
  { slug: 'prefetch', label: 'Prefetch' },
] as const;

/**
 * WAAP website detail — the per-website config screen reached via "Configure" on
 * the Websites list. A horizontal tab bar (Configuration / Origin / Access
 * Control / Cache Control / Redirection / SSL / CORS / Purge / Prefetch) over an
 * internal route per tab, matching the vendor portal layout. The website is
 * fixed by the `:domainId` route param (no global picker here).
 */
export function WaapWebsiteDetail() {
  const { serviceId, domainId } = useParams<{ serviceId: string; domainId: string }>();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const detailBase = `/services/waap/${serviceId}/websites/${domainId}`;
  const rest = pathname.startsWith(detailBase) ? pathname.slice(detailBase.length).replace(/^\//, '') : '';
  const active = (TABS.find((t) => t.slug === rest)?.slug ?? 'configuration') as string;

  // Domain name comes from the WAAP website registry already in context — no CDN
  // domain-detail call (which would trigger CDN aspect fetches for a WAAP domain).
  const { websites, isLoading } = useWaapWebsite();
  const title = websites.find((w) => w.domainId === domainId)?.domain ?? domainId ?? 'Website';

  return (
    <Container size="xl">
      <PageHeader
        breadcrumbs={
          <WaapConsoleBreadcrumbs trail={[{ label: 'Websites', slug: 'websites' }, { label: title }]} />
        }
        title={isLoading ? <Skeleton style={{ height: 24, width: 220 }} /> : title}
      />

      <Tabs value={active} onValueChange={(v) => navigate(`${detailBase}/${v}`)} style={{ marginBottom: 'var(--vn-space-5)' }}>
        <TabsList>
          {TABS.map((t) => (
            <TabsTrigger key={t.slug} value={t.slug}>
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <Routes>
        <Route index element={<Navigate to="configuration" replace />} />
        <Route path="configuration" element={<ConfigurationTab />} />
        <Route path="origin" element={<OriginTab />} />
        <Route path="access-control" element={<AccessControlTab />} />
        <Route path="cache-control" element={<CacheControlTab />} />
        <Route path="redirection" element={<RedirectionTab />} />
        <Route path="ssl" element={<SslTab />} />
        <Route path="cors" element={<CorsHeaderTab />} />
        <Route path="purge" element={<PurgeTab />} />
        <Route path="prefetch" element={<PrefetchTab />} />
        <Route path="*" element={<Navigate to="configuration" replace />} />
      </Routes>
    </Container>
  );
}
